/**
 * src/components/users/ChangePasswordModal.tsx
 * Ultra-Premium Apple iOS 18 Glassmorphic Password Change / Reset Modal
 * Supports both Admin resetting a member's password and Self-Service password change.
 */

import React, { useState, useEffect } from 'react';
import { 
  KeyRound, 
  Lock, 
  Eye, 
  EyeOff, 
  Sparkles, 
  Copy, 
  Check, 
  ShieldCheck, 
  AlertCircle 
} from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';
import { useToast } from '../../context/ToastContext';
import { api, type UserItem } from '../../services/api';
import { haptic, playTapSound, playSuccessSound } from '../../utils/audioHaptics';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser?: UserItem | null;
  isSelf?: boolean;
  onSuccess?: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  targetUser,
  isSelf = false,
  onSuccess
}) => {
  const { success, error: toastError } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [copiedGenerated, setCopiedGenerated] = useState(false);

  // Mode: Admin resetting someone else vs User changing own password
  const isAdminReset = Boolean(targetUser && !isSelf);

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setFormError('');
      setCopiedGenerated(false);
    }
  }, [isOpen, targetUser]);

  // Generate strong yet readable password
  const handleGeneratePassword = () => {
    haptic.tap();
    playTapSound();
    const prefixes = ['CKPass', 'Smart', 'Ledger', 'Audit', 'Secure'];
    const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomDigits = Math.floor(1000 + Math.random() * 9000);
    const symbols = ['@', '#', '!', '$'];
    const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
    const generated = `${randomPrefix}${randomSymbol}${randomDigits}`;

    setNewPassword(generated);
    setConfirmPassword(generated);
    setShowNew(true);
    setShowConfirm(true);
    setFormError('');

    // Copy to clipboard automatically for convenient sharing
    try {
      navigator.clipboard.writeText(generated);
      setCopiedGenerated(true);
      setTimeout(() => setCopiedGenerated(false), 2500);
    } catch {}
  };

  const handleCopyPassword = () => {
    if (!newPassword) return;
    haptic.tap();
    try {
      navigator.clipboard.writeText(newPassword);
      setCopiedGenerated(true);
      setTimeout(() => setCopiedGenerated(false), 2500);
    } catch {}
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!isAdminReset && !currentPassword) {
      setFormError('Please enter your current password.');
      return;
    }

    if (newPassword.length < 4) {
      setFormError('New password must be at least 4 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('New password and confirm password do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isAdminReset && targetUser) {
        // Admin resetting a member's password
        await api.users.changePassword(targetUser.id, newPassword);
        haptic.success();
        playSuccessSound();
        success(
          `Password for "${targetUser.full_name || targetUser.username}" was updated successfully!`,
          'Password Changed'
        );
      } else {
        // User changing their own password
        await api.auth.changePassword({
          new_password: newPassword,
          current_password: currentPassword || undefined
        });
        haptic.success();
        playSuccessSound();
        success('Your password has been changed successfully!', 'Security Updated');
      }

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setFormError(err.message || 'Failed to update password');
      toastError(err.message || 'Failed to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Password strength helper
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return null;
    if (pwd.length < 4) return { label: 'Too short', color: 'var(--ios-red)', width: '25%' };
    if (pwd.length < 6) return { label: 'Fair', color: 'var(--ios-orange)', width: '50%' };
    if (pwd.length < 8 || !/[0-9]/.test(pwd)) return { label: 'Good', color: 'var(--ios-blue)', width: '75%' };
    return { label: 'Strong', color: 'var(--ios-green)', width: '100%' };
  };

  const strength = getPasswordStrength(newPassword);

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={isAdminReset ? `Change Password` : 'Change Password'}
      maxWidth="460px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Target User Banner (if Admin Mode) */}
        {isAdminReset && targetUser && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 14px',
              borderRadius: '16px',
              background: 'rgba(10, 132, 255, 0.08)',
              border: '1px solid rgba(10, 132, 255, 0.2)'
            }}
          >
            <div
              className={`ios-icon-squircle ${targetUser.role === 'admin' ? 'blue' : 'green'}`}
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '13px',
                fontSize: '1rem',
                fontWeight: 800,
                overflow: 'hidden'
              }}
            >
              {targetUser.avatar_url ? (
                <img src={targetUser.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (targetUser.full_name || targetUser.username).charAt(0).toUpperCase()
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {targetUser.full_name || targetUser.username}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                @{targetUser.username} • <span style={{ textTransform: 'capitalize' }}>{targetUser.role}</span>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(10, 132, 255, 0.15)',
              color: 'var(--ios-blue)',
              fontSize: '0.72rem',
              fontWeight: 700
            }}>
              <KeyRound size={12} />
              <span>Reset</span>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {formError && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '12px',
              background: 'rgba(255, 69, 58, 0.12)',
              border: '1px solid rgba(255, 69, 58, 0.3)',
              color: 'var(--ios-red)',
              fontSize: '0.8125rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            <span>{formError}</span>
          </div>
        )}

        {/* Current Password Field (Only when user is changing their own password) */}
        {!isAdminReset && (
          <div>
            <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              Current Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrent ? 'text' : 'password'}
                className="glass-input"
                style={{ width: '100%', paddingRight: '40px', fontSize: '0.9rem', borderRadius: '12px' }}
                placeholder="Enter your current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoFocus
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title={showCurrent ? 'Hide password' : 'Show password'}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        )}

        {/* Quick Password Generator Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            New Password
          </label>
          <button
            type="button"
            onClick={handleGeneratePassword}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--ios-blue)',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 4px'
            }}
            title="Generate a secure, random password"
          >
            <Sparkles size={12} />
            <span>Suggest Strong Password</span>
          </button>
        </div>

        {/* New Password Input */}
        <div style={{ position: 'relative' }}>
          <input
            type={showNew ? 'text' : 'password'}
            className="glass-input"
            style={{ width: '100%', paddingRight: newPassword ? '72px' : '40px', fontSize: '0.9rem', borderRadius: '12px' }}
            placeholder="Minimum 4 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus={isAdminReset}
            required
          />
          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {newPassword && (
              <button
                type="button"
                onClick={handleCopyPassword}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: copiedGenerated ? 'var(--ios-green)' : 'var(--text-tertiary)',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Copy password to clipboard"
              >
                {copiedGenerated ? <Check size={15} /> : <Copy size={15} />}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title={showNew ? 'Hide password' : 'Show password'}
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Password Strength Indicator */}
        {strength && (
          <div style={{ marginTop: '-8px' }}>
            <div style={{ height: '3px', width: '100%', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: strength.width, background: strength.color, transition: 'all 0.3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.7rem', color: strength.color, fontWeight: 600 }}>
              <span>Security: {strength.label}</span>
              {copiedGenerated && <span style={{ color: 'var(--ios-green)' }}>✓ Copied to clipboard!</span>}
            </div>
          </div>
        )}

        {/* Confirm Password Field */}
        <div>
          <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
            Confirm New Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirm ? 'text' : 'password'}
              className="glass-input"
              style={{
                width: '100%',
                paddingRight: '40px',
                fontSize: '0.9rem',
                borderRadius: '12px',
                borderColor: confirmPassword && newPassword !== confirmPassword ? 'var(--ios-red)' : undefined
              }}
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {confirmPassword && newPassword === confirmPassword && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--ios-green)', marginTop: '4px', fontWeight: 600 }}>
              <ShieldCheck size={13} />
              <span>Passwords match</span>
            </div>
          )}
        </div>

        {/* Modal Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <GlassButton
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
            style={{ flex: 1 }}
          >
            Cancel
          </GlassButton>
          <GlassButton
            type="submit"
            variant="primary"
            disabled={isSubmitting || !newPassword || newPassword !== confirmPassword}
            style={{ flex: 1.3 }}
          >
            {isSubmitting ? 'Saving...' : isAdminReset ? 'Set User Password' : 'Update Password'}
          </GlassButton>
        </div>
      </form>
    </GlassModal>
  );
};
